/**
 * Report-problems module API client (T35.6.3).
 *
 * A thin wrapper for the standalone `/api/report-problems` endpoint.
 * Self-contained inside the Report Problems module
 * (ADR-012 D1); nothing unrelated reaches these endpoints.
 *
 * Auth: app bootstrap injects the shared HttpClient, including its native-aware
 * TokenProvider and refresh-and-retry path.
 */

import { ApiError, type HttpClient } from '@shopista/shared/api';

let client: HttpClient | null = null;

export function setReportProblemsHttpClient(httpClient: HttpClient): void {
  client = httpClient;
}

function http(): HttpClient {
  if (!client) throw new Error('Report Problems HTTP client has not been configured');
  return client;
}

function legacyError(error: unknown, fallback: string): never {
  if (error instanceof ApiError) {
    const err = new Error(fallback) as Error & { status?: number };
    err.status = error.status;
    throw err;
  }
  throw error;
}

// ---------------------------------------------------------------------------
// Severity vocabulary — matches the server's REPORT_PROBLEM_SEVERITIES check.
// ---------------------------------------------------------------------------
export const REPORT_PROBLEM_SEVERITIES = ['minor', 'major', 'blocked'] as const;
export type ReportProblemSeverity = (typeof REPORT_PROBLEM_SEVERITIES)[number];

// ---------------------------------------------------------------------------
// Item-context kinds — each contextual reporter stamps its kind onto the report.
// ---------------------------------------------------------------------------
export interface DealItemContext {
  kind: 'deal';
  offerId: string | null;
  label: string;
}
export interface ProductSearchItemContext {
  kind: 'productsearch';
  searchTerm: string;
  surface: string;
}
export type ReportItemContext = DealItemContext | ProductSearchItemContext;

// ---------------------------------------------------------------------------
// Submit shape — what the sheet posts.
// ---------------------------------------------------------------------------
export interface ReportProblemSubmission {
  /** Client-minted UUID to prevent duplicate records on retry. */
  idempotencyKey: string;
  note: string | null;
  severity: ReportProblemSeverity | null;
  screenContext: string | null;
  /** The entity this report is about (deal or product-search), if any. */
  itemContext: ReportItemContext | null;
  /** App environment — build, device, online state. Kind-independent sibling. */
  env: { appBuild: string; device: string; online: boolean } | null;
  screenshots: Record<string, unknown> | null;
  isConsentToContact?: boolean;
}

export interface ReportProblemSubmitResponse {
  id: string;
  created: boolean;
}

/**
 * POST one report to `/api/report-problems`. Resolves on 2xx (201 fresh insert,
 * 200 resend already recorded); rejects on any other status. A 4xx is NOT
 * retryable — `.status` is set on the error so the outbox flush can drop it.
 */
export async function postReportProblem(
  submission: ReportProblemSubmission,
): Promise<ReportProblemSubmitResponse> {
  try { return await http().postRaw('/api/report-problems', submission) as ReportProblemSubmitResponse; }
  catch (error) { return legacyError(error, `POST /api/report-problems failed: ${(error as ApiError).status}`); }
}

/**
 * GET the household's own reports (newest-first) from `/api/report-problems`.
 * Kept here in case consumers need the read; the "Your reports" list was removed
 * as part of T35.6.3 cleanup.
 */
export interface HouseholdReportProblem {
  id: string;
  note: string | null;
  severity: string | null;
  screenContext: string | null;
  createdAt: string;
}

export async function getHouseholdReportProblems(): Promise<HouseholdReportProblem[]> {
  let body: { reports: HouseholdReportProblem[] };
  try { body = await http().getRaw('/api/report-problems') as typeof body; }
  catch (error) { return legacyError(error, `GET /api/report-problems failed: ${(error as ApiError).status}`); }
  return body.reports;
}
